import { SceneService } from '@/modules/databaseService/types';
import type { RendererBridge } from '@/modules/rendererBridge';
import { NavigateFunction } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { AssetServiceImpl } from '@/modules/databaseService/assets';
import { AssetAdapter } from './AssetAdapter';
import { ProjectAdapter } from './ProjectAdapter';
import { ClipAdapter } from './ClipAdapter';
import { ClipServiceImpl } from '@/modules/databaseService/clips';
import { reconcileStoryAttributionsForScene } from '@/modules/library/utils/storyAttributionUtils';
import { ASSET_CATEGORIES, ENABLE_CAMERA } from '@/types/assetTypes';
import { PublishedStoryDurationService } from '@/modules/storyData/PublishedStoryDurationService';
import type { IAnalyticsService } from '@/modules/analytics';
import type { OriginShareLinks, ShareLinkDto } from '@/modules/databaseService/scenes/shareLinks';
import { metresBetween, processGeocodingResult } from '@/utils/locationUtils';
import { safeParseJsonObject } from '@/utils/safeParseJson';
import { anchorThumbnailMetadata, isAnchorThumbnail } from '@/modules/uiImages/scanImages';
import { isPlaceholderThumbnailUrl } from '@/constants/placeholderThumbnails';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import { imageCreateBody } from '@/infrastructure/api/v1/mappers/images';
import { scenePatchBody, type LegacyScene, type SceneStatus } from '@/infrastructure/api/v1/mappers/scenes';
import { storyCreateBody } from '@/infrastructure/api/v1/mappers/stories';
import {
  changeSceneStatus,
  fetchSceneRow,
  fetchSceneRows,
  fetchStoryRow,
  updateStoryRow,
} from '@/modules/databaseService/scenes';
import { refreshProjectSummary } from '@/modules/databaseService/projects';

import type { WriteScope } from '@/modules/undoRedo';
const ANCHOR_ASSET_CATEGORIES = ['immersal', 'image_anchor', 'geospatial_anchor'];

/** How far from the story's stored point an anchor stands at another place, whose address and city it then takes. */
const ANCHOR_MOVED_METRES = 100;

/** What the story's location says of the place, rather than of the point. */
const PLACE_FIELDS = ['address', 'city', 'starting_point'];

/** Writes legacy scene_instances columns to one scene version (updateScene). */
function updateSceneRow(sceneId: string, changes: Partial<LegacyScene>, scope?: WriteScope) {
  return toResult(v1.PATCH('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneId } }, body: scenePatchBody(changes), headers: scope?.headers }));
}

/** Every asset instance of a scene in the given categories, as legacy rows in creation order. */
async function fetchSceneAssetsByCreation(sceneId: string, category?: string[]) {
  const result = await listAll(page => v1.GET('/api/v1/asset-instances', {
    params: { query: { sceneUniqueId: [sceneId], category, sort: 'createdAt', ...page } },
  }));
  return { data: result.data?.map(assetInstanceToLegacy) ?? null, error: result.error };
}

export class SceneAdapter implements SceneService {
  private projectService: ProjectAdapter;
  private assetService: AssetServiceImpl;
  private clipService: ClipServiceImpl;
  private publishedStoryDurationService?: PublishedStoryDurationService;

  constructor(
    bridge: RendererBridge,
    private readonly analytics?: IAnalyticsService
  ) {
    this.projectService = new ProjectAdapter(this);
    const clipAdapter = new ClipAdapter(bridge);
    this.clipService = new ClipServiceImpl(clipAdapter);
    this.assetService = new AssetServiceImpl(new AssetAdapter(bridge), this.clipService);
    // Bind so the method can be passed as a callback without losing `this`
    this.updateSceneAnchorCategory = this.updateSceneAnchorCategory.bind(this);
  }

  // Method to inject the PublishedStoryDurationService after construction
  setPublishedStoryDurationService(service: PublishedStoryDurationService): void {
    this.publishedStoryDurationService = service;
  }

  /**
   * Recalculate and update project total duration. The server computes and stores it with the
   * project's other summary fields (refreshProjectSummary).
   * @param projectId - The project ID to update
   * @param context - Context for logging (e.g., "after creating copy", "after scene creation")
   * @returns Object with totalDurationSeconds
   */
  private async recalculateAndUpdateProjectDuration(
    projectId: string,
    context: string,
  ): Promise<{ totalDurationSeconds: number }> {
    try {
      const { data: project, error: updateError } = await refreshProjectSummary(projectId);

      if (updateError) {
        console.error(`Error updating project total duration ${context}:`, updateError);
        return { totalDurationSeconds: 0 };
      }

      const totalDurationSeconds = project.project_instance_total_duration ?? 0;
      console.log(`✅ [SceneAdapter] Updated project ${projectId} total duration to ${totalDurationSeconds} seconds ${context}`);
      return { totalDurationSeconds };
    } catch (durationError) {
      console.error(`Error recalculating project duration ${context}:`, durationError);
      // Don't throw here as the main operation was successful
      return { totalDurationSeconds: 0 };
    }
  }

  async deleteSceneInstanceRow(id: string) {
    try {
      // First get the project ID before deleting the scene
      const { data: sceneData, error: fetchError } = await fetchSceneRow(id);

      if (fetchError) {
        throw fetchError;
      }

      const projectId = sceneData.project_instance_id;

      // Delete the scene; the server recounts the project in the same transaction
      const { error } = await toResult(v1.DELETE('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: id } } }));

      if (error) {
        throw error;
      }

      console.log('Row deleted successfully');

      this.analytics?.trackLureoSceneDeleted({
        scene_instance_id: id,
        project_id: projectId ?? undefined,
      });
    } catch (error) {
      console.error('Error deleting row:', error);
      throw error;
    }
  }

  async updateSceneCoverImage(sceneInstanceId: string, imageUrl: string): Promise<void> {
    try {
      const { error } = await updateSceneRow(sceneInstanceId, { scene_instance_cover_img_url: imageUrl });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating scene cover image:', error);
      throw error;
    }
  }

  // Archives a version; the server unpublishes the project when no live version is left (changeSceneStatus)
  async handleSceneArchive(sceneInstanceId: string) {
    try {
      const { error } = await changeSceneStatus(sceneInstanceId, 'archived');

      if (error) {
        console.error('Error changing scene status:', error.message);
        return {
          success: false,
          message: `Error: ${error.message}`,
          scene_instance_id: sceneInstanceId
        };
      }

      return {
        success: true,
        message: 'Scene archived successfully',
        scene_instance_id: sceneInstanceId
      };
    } catch (error) {
      console.error('Unexpected error in handleSceneArchive:', error);
      return {
        success: false,
        message: `Unexpected error: ${error instanceof Error ? error.message : String(error)}`,
        scene_instance_id: sceneInstanceId
      };
    }
  }

  /**
   * Moves a version to `sceneStatus` (submitting it for review when pending). The server archives an
   * older version still in review, numbers the version, opens the review submission and updates the
   * project's published flag in one transaction (changeSceneStatus); the live version stays live
   * until the new one is approved.
   */
  // A pending move without versionNumber is numbered by the server, under the story lock
  async handleScenePublish(
    sceneStatus: 'live' | 'pending' | 'archived' | 'draft' | 'rejected' | 'locked',
    sceneInstanceId: string,
    versionNumber?: number
  ) {
    try {
      console.log('handleScenePublish called with:', { sceneStatus, sceneInstanceId, versionNumber });

      // v1 has no rejected status; the server refuses one as an invalid value
      const { data: scene, error } = await changeSceneStatus(sceneInstanceId, sceneStatus as SceneStatus, versionNumber);

      if (error) {
        console.error('Error changing scene status:', error);
        return {
          success: false,
          message: `Error: ${error.status} ${error.message}`,
          reason: error.detail || error.message,
          scene_instance_id: sceneInstanceId || ''
        };
      }

      const projectId = scene.project_instance_id;

      // Calculate and update scene instance duration
      if (this.publishedStoryDurationService) {
        try {
          const durationResult = await this.publishedStoryDurationService.calculateAndUpdateSceneInstanceDuration(sceneInstanceId);
          if (durationResult.success) {
            console.log(`Scene instance ${sceneInstanceId} duration updated to ${durationResult.durationMs}ms`);
          } else {
            console.warn(`Failed to update duration for scene instance ${sceneInstanceId}`);
          }
        } catch (durationError) {
          console.error('Error calculating/updating scene instance duration:', durationError);
          // Don't fail the publish operation if duration update fails
        }
      } else {
        console.warn('PublishedStoryDurationService not available for duration calculation');
      }

      try {
        await reconcileStoryAttributionsForScene(sceneInstanceId);
      } catch (attributionError) {
        console.warn('Failed to reconcile story attributions after publishing:', attributionError);
      }

      const publishedVersion = scene.scene_instance_version_number ?? versionNumber;
      this.analytics?.trackLureoPublishedScene({
        scene_instance_id: sceneInstanceId,
        publish_type: publishedVersion === 1 ? 'first_publish' : 're_publish',
        scene_version: publishedVersion,
        project_id: projectId ?? undefined,
      });

      return {
        success: true,
        message: 'Scene published successfully',
        scene_instance_id: sceneInstanceId || '',
        version_number: publishedVersion,
      };
    } catch (error) {
      console.error(`Unexpected error in handleScenePublish:`, error);
      return {
        success: false,
        message: `Unexpected error: ${error instanceof Error ? error.message : String(error)}`,
        scene_instance_id: sceneInstanceId || ''
      };
    }
  }

  // A story under review has a live and a pending version at once; count stories, not versions
  async countPublishedStories(userId: string): Promise<number> {
    const { data, error } = await fetchSceneRows({ userUniqueId: userId, status: ['live', 'pending'] });
    if (error) throw error;
    return new Set(data.map((row) => row.scene_origin_id)).size;
  }

  async createNewScene(
    userId: string,
    navigate: NavigateFunction,
    projectInstanceId?: string,
    projectInitials?: string,
    sceneInstancePayload?: Record<string, unknown>,
    options?: { skipNavigate?: boolean }
  ): Promise<{ sceneInstanceId: string; sceneOriginId: string } | undefined> {
    if (projectInstanceId) {
      // The story and its first version (payload drives device-specific editor behaviour) in one call.
      // The server numbers and names the story after the project's last one and recounts the project.
      const body = storyCreateBody(
        { scene_origin_id: uuid(), project_instance_id: projectInstanceId },
        {
          scene_instance_id: uuid(),
          scene_instance_anchor_category: 'place_anywhere',
          ...(sceneInstancePayload && Object.keys(sceneInstancePayload).length > 0 && { scene_instance_payload: sceneInstancePayload as never })
        },
      );
      const { data, error } = await toResult(v1.POST('/api/v1/stories', { body }));

      if (error) {
        throw error;
      }

      const sceneOriginId = data.story.uniqueId;
      const sceneInstanceId = data.scene.uniqueId;

      // CAMERA_FEATURE: disabled — set ENABLE_CAMERA to true to re-enable
      if (ENABLE_CAMERA) {
        await this.assetService.addInitialCameraToScene(userId, sceneInstanceId);
      }

      this.analytics?.trackLureoNewSceneCreated({
        project_id: projectInstanceId,
        scene_instance_id: sceneInstanceId,
        from_template: false,
      });

      if (sceneInstanceId && !options?.skipNavigate) {
        navigate(`/editor/${sceneInstanceId}`);
      }

      return sceneInstanceId ? { sceneInstanceId, sceneOriginId } : undefined;
    } else {
      await this.projectService.createNewProject(userId, navigate, projectInitials || 'New Project');
      return undefined;
    }
  }

  async updateSceneInstancePayload(sceneInstanceId: string, payload: Record<string, unknown>, scope?: WriteScope): Promise<void> {
    try {
      const { error } = await updateSceneRow(sceneInstanceId, { scene_instance_payload: payload as never }, scope);

      if (error) {
        console.error('Error updating scene instance payload:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error updating scene instance payload:', error);
      throw error;
    }
  }

  async fetchSceneAnchorInstance(sceneInstanceId: string, category: 'immersal' | 'geospatial_anchor' | 'image_anchor') {
    console.log(`Searching for ${category} anchor with scene ID:`, sceneInstanceId);
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/asset-instances', {
        params: { query: { sceneUniqueId: [sceneInstanceId], category: [category], limit: 1, offset: 0 } },
      }));

      if (error) {
        console.error(`Error fetching ${category} anchor:`, error);
        return null;
      }

      const anchor = data.items[0];
      return anchor ? assetInstanceToLegacy(anchor) : null;
    } catch (error) {
      console.error(`Error fetching ${category} anchor:`, error);
      return null;
    }
  }

  async checkSceneAnchors(sceneInstanceId: string) {
    console.log('Checking scene anchors for scene ID:', sceneInstanceId);
    try {
      const { data, error } = await fetchSceneAssetsByCreation(sceneInstanceId, ['immersal', 'image_anchor', 'geospatial_anchor', 'tap_to_place']);

      if (error) {
        console.error('Error checking scene anchors:', error);
        return { scan: false, imageAnchor: false, geospatial: false, tapToPlace: false };
      }

      // Map the database categories to more descriptive names
      return {
        scan: data?.some(item => item.asset_instance_category === ASSET_CATEGORIES.IMMERSAL) ?? false,
        imageAnchor: data?.some(item => item.asset_instance_category === 'image_anchor') ?? false,
        geospatial: data?.some(item => item.asset_instance_category === ASSET_CATEGORIES.GEOSPATIAL) ?? false,
        tapToPlace: data?.some(item => item.asset_instance_category === ASSET_CATEGORIES.TAP_TO_PLACE) ?? false
      };
    } catch (error) {
      console.error('Error in checkSceneAnchors:', error);
      return { scan: false, imageAnchor: false, geospatial: false, tapToPlace: false };
    }
  }

  async updateSceneAnchorCategory(sceneInstanceId: string, scope?: WriteScope) {
    try {
      // Check what anchor assets the scene has
      const { data, error } = await fetchSceneAssetsByCreation(sceneInstanceId, ANCHOR_ASSET_CATEGORIES);

      if (error) {
        console.error('❌ [SceneAdapter] Error checking for anchor assets:', error);
        return;
      }

      // Determine the appropriate anchor category based on what assets are present
      let newAnchorCategory: 'place_anywhere' | 'immersal' | 'image_anchor' | 'geospatial_anchor' = 'place_anywhere';

      if (data && data.length > 0) {
        // Check for immersal assets first (highest priority)
        const hasImmersal = data.some(item => item.asset_instance_category === ASSET_CATEGORIES.IMMERSAL);
        const hasImageAnchor = data.some(item => item.asset_instance_category === 'image_anchor');
        const hasGeospatial = data.some(item => item.asset_instance_category === ASSET_CATEGORIES.GEOSPATIAL);

        if (hasImmersal) {
          newAnchorCategory = 'immersal';
        } else if (hasImageAnchor) {
          newAnchorCategory = 'image_anchor';
        } else if (hasGeospatial) {
          newAnchorCategory = 'geospatial_anchor';
        }
      }

      const { error: updateError } = await updateSceneRow(sceneInstanceId, { scene_instance_anchor_category: newAnchorCategory }, scope);

      if (updateError) {
        console.error('❌ [SceneAdapter] Error updating scene anchor category:', updateError);
      } else {
        console.log(`✅ [SceneAdapter] Successfully updated scene ${sceneInstanceId} anchor category to: ${newAnchorCategory}`);

        // Verify the update by fetching the current value
        const { error: verifyError } = await fetchSceneRow(sceneInstanceId);

        if (verifyError) {
          console.error('❌ [SceneAdapter] Error verifying update:', verifyError);
        }
      }

      if (newAnchorCategory === 'geospatial_anchor' || newAnchorCategory === 'immersal') {
        try {
          await this.seedScanImageFromAnchor(sceneInstanceId, newAnchorCategory);
        } catch (seedError) {
          console.warn('Failed to seed scan image from anchor:', seedError);
        }
        try {
          await this.syncSceneOriginLocationFromAnchor(sceneInstanceId, newAnchorCategory, scope);
        } catch (syncError) {
          console.warn('Failed to sync scene origin location from anchor:', syncError);
        }
      }
    } catch (error) {
      console.error('❌ [SceneAdapter] Error in updateSceneAnchorCategory:', error);
    }
  }

  private async syncSceneOriginLocationFromAnchor(
    sceneInstanceId: string,
    category: 'geospatial_anchor' | 'immersal',
    scope?: WriteScope,
  ): Promise<void> {
    // The most recently placed anchor of the category: the last in creation order
    const { data: anchors } = await fetchSceneAssetsByCreation(sceneInstanceId, [category]);
    const latest = anchors?.[anchors.length - 1];

    // A scan placed without GPS has no location to give the story
    if (latest?.asset_instance_latitude == null || latest.asset_instance_longitude == null) return;
    const lat = Number(latest.asset_instance_latitude);
    const lng = Number(latest.asset_instance_longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const { data: sceneRow } = await fetchSceneRow(sceneInstanceId);
    if (!sceneRow?.scene_origin_id) return;
    const { data: story, error: storyError } = await fetchStoryRow(sceneRow.scene_origin_id);
    if (storyError) {
      console.warn('[syncSceneOriginLocationFromAnchor] failed to read the story:', storyError);
      return;
    }
    const stored = safeParseJsonObject(story.scene_origin_location_info);

    // An anchor at another place than the stored point takes that place's address and city; near it, the creator's address, city and starting point stay
    const was = stored.coordinates as { lat?: unknown; lng?: unknown } | undefined;
    const moved = typeof was?.lat === 'number' && typeof was?.lng === 'number' && metresBetween({ lat: was.lat, lng: was.lng }, { lat, lng }) > ANCHOR_MOVED_METRES;
    const existing = moved ? Object.fromEntries(Object.entries(stored).filter(([key]) => !PLACE_FIELDS.includes(key))) : stored;

    // A reverse geocode fills only what is missing
    let place: { address: string | null; city: string | null } | null = null;
    if ((!existing.address || !existing.city) && typeof google !== 'undefined' && google.maps?.Geocoder) {
      try {
        const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          new google.maps.Geocoder().geocode({ location: { lat, lng } }, (res, status) =>
            status === 'OK' && res ? resolve(res) : reject(status)
          );
        });
        if (results[0]) place = processGeocodingResult(results[0]);
      } catch (e) {
        console.warn('[syncSceneOriginLocationFromAnchor] reverse geocode failed:', e);
      }
    }
    const address = existing.address || place?.address;
    const city = existing.city || place?.city;
    const locationInfo = { ...existing, ...(address && { address }), ...(city && { city }), coordinates: { lat, lng } };
    if (JSON.stringify(locationInfo) === JSON.stringify(stored)) return;

    // In the gesture that placed the anchor, so undoing it puts the story's location back
    await updateStoryRow(sceneRow.scene_origin_id, { scene_origin_location_info: JSON.stringify(locationInfo) }, scope);
  }

  // The story's scan image from the newest anchor's thumbnail, never replacing one the creator supplied in any version
  private async seedScanImageFromAnchor(
    sceneInstanceId: string,
    category: 'geospatial_anchor' | 'immersal',
  ): Promise<void> {
    const { data: anchors, error: anchorsError } = await fetchSceneAssetsByCreation(sceneInstanceId, [ASSET_CATEGORIES.IMMERSAL, ASSET_CATEGORIES.GEOSPATIAL]);
    if (anchorsError) {
      console.warn('[seedScanImageFromAnchor] failed to fetch anchors:', anchorsError);
      return;
    }

    // Newest first, real images only: no Street View imagery means no thumbnail, and legacy rows may hold the map-pin placeholder
    const withThumbnail = (anchors ?? [])
      .filter((a) => !!a.asset_instance_img_url && !isPlaceholderThumbnailUrl(a.asset_instance_img_url))
      .reverse();
    const seed = withThumbnail.find((a) => a.asset_instance_category === category) ?? withThumbnail[0];
    if (!seed) return;
    const seedUrl = seed.asset_instance_img_url as string;

    const { data: sceneRow } = await fetchSceneRow(sceneInstanceId);
    const storyId = sceneRow?.scene_origin_id;
    if (!storyId) return;

    // Newest first, as phones and the story page pick the story's scan image
    const { data: scans, error: listError } = await listAll(page => v1.GET('/api/v1/images', {
      params: { query: { sceneOriginUniqueId: [storyId], category: ['ScanImage'], sort: '-createdAt', ...page } },
    }));
    if (listError) {
      console.warn('[seedScanImageFromAnchor] failed to list the story\'s scan images:', listError);
      return;
    }
    const anchorUrls = new Set(withThumbnail.map((a) => a.asset_instance_img_url as string));
    if (scans.some((image) => (image.croppedUrl || image.originalUrl) && !isAnchorThumbnail(image, anchorUrls))) return;
    if (scans.find((image) => image.originalUrl)?.originalUrl === seedUrl) return;

    // Every scan image the story has left is an anchor's thumbnail, so the new one replaces them all
    const { error: insertError } = await toResult(v1.POST('/api/v1/images', {
      body: imageCreateBody({
        image_id: uuid(),
        scene_instance_id: sceneInstanceId,
        scene_origin_id: storyId,
        project_instance_id: sceneRow?.project_instance_id,
        image_category: 'ScanImage',
        image_original_url: seedUrl,
        image_metadata: JSON.stringify(anchorThumbnailMetadata(seedUrl)),
      }, { replace: true }),
    }));
    if (insertError) {
      console.warn('[seedScanImageFromAnchor] failed to write the scan image:', insertError);
    }
  }

  // Phase 1: Core Operations moved from StoryEdit.tsx
  async makeEditableCopy(userId: string, sceneInstanceId: string, projectId: string): Promise<{ sceneInstanceId: string; versionNumber: number }> {
    const { data, error } = await toResult(v1.POST('/api/v1/scenes/{sceneUniqueId}/editable-copy', {
      params: { path: { sceneUniqueId: sceneInstanceId } },
    }));
    if (error) {
      throw new Error(`makeEditableCopy failed: ${error.message}`, { cause: error });
    }
    // A copy is always numbered: it is the story's next version
    const copy = { sceneInstanceId: data.uniqueId, versionNumber: data.versionNumber as number };

    // CAMERA_FEATURE: disabled — set ENABLE_CAMERA to true to re-enable
    if (ENABLE_CAMERA) {
      await this.assetService.addInitialCameraToScene(userId, copy.sceneInstanceId);
    }

    await this.recalculateAndUpdateProjectDuration(projectId, 'after creating copy');

    return copy;
  }

  async unpublishVersion(userId: string, sceneOriginId: string, versionNumber: number, projectId: string): Promise<void> {
    try {
      // Move the version from 'live' or 'pending' to 'draft'; the server unpublishes the project
      // when no live version is left
      const { data: candidates, error: fetchError } = await fetchSceneRows({ storyUniqueId: [sceneOriginId], status: ['live', 'pending'] });

      if (fetchError) {
        console.error('Error unpublishing scene:', fetchError);
        throw new Error('Failed to unpublish version', { cause: fetchError });
      }

      const targets = candidates.filter((scene) => scene.scene_instance_version_number === versionNumber);
      if (targets.length === 0) {
        console.error('No live or pending scene found to unpublish for version:', versionNumber);
        throw new Error('No live or pending scene found to unpublish');
      }

      for (const target of targets) {
        const { error: updateError } = await changeSceneStatus(target.scene_instance_id, 'draft');
        if (updateError) {
          console.error('Error unpublishing scene:', updateError);
          throw new Error('Failed to unpublish version', { cause: updateError });
        }
      }

      console.log('Successfully unpublished version:', versionNumber, 'for scene origin:', sceneOriginId);

      this.analytics?.trackLureoSceneUnpublished({
        scene_instance_id: targets[0].scene_instance_id,
        project_id: projectId,
        scene_version: versionNumber,
      });
    } catch (error) {
      console.error('Error performing unpublish version:', error);
      throw error;
    }
  }

  async archiveDraftVersion(userId: string, sceneInstanceId: string): Promise<void> {
    try {
      // Only a draft or template version is archived here; the server keeps the project's published flag
      const { data: scene, error: fetchError } = await fetchSceneRow(sceneInstanceId);
      const archivable = !fetchError && (scene.scene_instance_status === 'draft' || scene.scene_instance_status === 'template');
      const { error: updateError } = archivable
        ? await changeSceneStatus(sceneInstanceId, 'archived')
        : { error: fetchError ?? new Error('Not a draft or template version') };

      if (updateError) {
        console.error('Error archiving draft:', updateError);
        throw new Error('Failed to archive draft version', { cause: updateError });
      }

      console.log('Successfully archived draft version:', sceneInstanceId);
    } catch (error) {
      console.error('Error archiving draft version:', error);
      throw error;
    }
  }

  // Phase 2: Helper Methods moved from StoryEdit.tsx
  async checkProjectHasOtherLiveScenes(projectId: string, currentSceneId?: string): Promise<boolean> {
    if (!projectId) return false;

    try {
      const { data: liveScenes, error } = await fetchSceneRows({ projectUniqueId: [projectId], status: ['live'] });

      if (error) {
        console.error('Error checking for other live scenes:', error);
        return false;
      }

      // Only exclude current scene if it exists
      return liveScenes.some((scene) => !currentSceneId || scene.scene_instance_id !== currentSceneId);
    } catch (error) {
      console.error('Error checking project live status:', error);
      return false;
    }
  }

  async checkUnpublishWillMakeProjectDraft(projectId: string, targetSceneId: string): Promise<boolean> {
    if (!projectId) return false;

    try {
      const { data: liveScenes, error } = await fetchSceneRows({ projectUniqueId: [projectId], status: ['live'] });

      if (error) {
        console.error('Error checking project live scenes:', error);
        return true;
      }

      return liveScenes.length === 1 && liveScenes[0].scene_instance_id === targetSceneId;
    } catch (error) {
      console.error('Error checking if unpublish will make project draft:', error);
      return true;
    }
  }

  async updateSceneOriginName(sceneOriginId: string, newName: string, scope?: WriteScope) {
    try {
      const { data, error } = await updateStoryRow(sceneOriginId, { scene_origin_name: newName }, scope);

      if (error) {
        throw error;
      }

      return data.scene_origin_name;
    } catch (error) {
      console.error('Error updating scene origin name:', error);
      throw error;
    }
  }

  // The listed stories take 1..n and the rest of the project follow, in one server transaction (reorderStories)
  async reorderStories(projectId: string, storyIds: string[]): Promise<void> {
    const { error } = await toResult(v1.POST('/api/v1/stories/reorder', { body: { projectUniqueId: projectId, storyUniqueIds: storyIds } }));
    if (error) throw error;
  }

  async discardDraft(sceneInstanceId: string): Promise<void> {
    const { error } = await toResult(v1.DELETE('/api/v1/scenes/{sceneUniqueId}/draft', { params: { path: { sceneUniqueId: sceneInstanceId } } }));
    if (error) throw error;
  }

  // Deletes the story with every version and its content, renumbers the project's remaining stories
  // and recounts the project, in one server transaction (deleteStory)
  async deleteSceneOriginRow(sceneOriginId: string) {
    try {
      const { error } = await toResult(v1.DELETE('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: sceneOriginId } } }));

      if (error) {
        throw error;
      }

      console.log('Scene origin row deleted successfully');
    } catch (error) {
      console.error('Error deleting scene origin row:', error);
      throw error;
    }
  }

  // These throw the ApiProblem itself, so callers can tell a takedown or a rate limit from anything else
  async getShareLinks(sceneOriginId: string): Promise<OriginShareLinks> {
    const { data, error } = await listAll(page => v1.GET('/api/v1/share-links', { params: { query: { storyUniqueId: sceneOriginId, ...page } } }));
    if (error) throw error;
    return { live: data.find(link => link.kind === 'live') ?? null, drafts: data.filter(link => link.kind === 'draft') };
  }

  async mintDraftShareLink(sceneInstanceId: string): Promise<ShareLinkDto> {
    const { data, error } = await toResult(v1.POST('/api/v1/share-links/ensure', { body: { kind: 'draft', sceneUniqueId: sceneInstanceId } }));
    if (error) throw error;
    return data;
  }

  async mintLiveShareLink(sceneOriginId: string): Promise<ShareLinkDto> {
    const { data, error } = await toResult(v1.POST('/api/v1/share-links/ensure', { body: { kind: 'live', storyUniqueId: sceneOriginId } }));
    if (error) throw error;
    return data;
  }

  async rotateShareLink(slug: string): Promise<ShareLinkDto> {
    const { data, error } = await toResult(v1.POST('/api/v1/share-links/{slug}/rotate', { params: { path: { slug } } }));
    if (error) throw error;
    return data;
  }

  async revokeShareLink(slug: string): Promise<void> {
    const { error } = await toResult(v1.POST('/api/v1/share-links/{slug}/revoke', { params: { path: { slug } } }));
    if (error) throw error;
  }
}